import { useCallback, useEffect, useRef } from 'react';
import {
  BackgroundJobKindEnum,
  BackgroundJobStatusEnum,
  useBackgroundJobProgressSubscription,
} from 'graphql-common';
import { useTabActivity } from '@lib/hooks/useTabActivity';
import { logger } from '@lib/services/logger';

const REFETCH_DEBOUNCE_MS = 1500;

// Kinds whose progress frames should re-pull `aiDetectionStatus`. Detection runs
// as a DOCUMENTATION_FIELD_DETECTION child, but a *cancel* stops the parent
// DOCUMENTATION_IMPORT job — its frame carries the parent kind (STOPPED) with
// the detection children nested under `node.children`, so we react to the import
// kind too, so a cancelled import re-pulls the now-CANCELLED field statuses.
const DETECTION_REFETCH_KINDS = new Set<BackgroundJobKindEnum>([
  BackgroundJobKindEnum.DocumentationFieldDetection,
  BackgroundJobKindEnum.DocumentationImport,
]);

// Minimal shape read off a backgroundJobProgress frame's node (and its
// children). Structural, so the generated subscription node type — whose
// children carry no further nesting — assigns cleanly, unlike Pick<BackgroundJob>.
type JobFrameNode = {
  kind: BackgroundJobKindEnum;
  status: BackgroundJobStatusEnum;
  children?: ReadonlyArray<{ kind: BackgroundJobKindEnum }> | null;
};

// A node is relevant when it (or any of its children) is an import or
// field-detection job.
function isDetectionFrame(node: JobFrameNode | null | undefined): boolean {
  if (!node) return false;
  const kinds = [
    node.kind,
    ...(node.children || []).map((child) => child.kind),
  ];
  return kinds.some((kind) => DETECTION_REFETCH_KINDS.has(kind));
}

type Args = {
  // Any field is still QUEUED/DETECTING — keep watching until it settles.
  isDetecting: boolean;
  refetch: () => void;
};

// Live-refreshes the document while AI field detection is in progress. Detection
// runs as a DOCUMENTATION_FIELD_DETECTION child on the session-wide job feed
// (BE guide §7/§9); we can't correlate a frame to this specific document, so any
// detection/import-progress frame schedules a debounced refetch, and the
// refetched `aiDetectionStatus` is the source of truth (including the terminal
// CANCELLED status the BE writes when detection is stopped). The subscription is
// only open while this document is detecting (skip otherwise).
export default function useDocumentDetectionRefetch({
  isDetecting,
  refetch,
}: Args) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleRefetch = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      refetch();
    }, REFETCH_DEBOUNCE_MS);
  }, [refetch]);

  useBackgroundJobProgressSubscription({
    skip: !isDetecting,
    fetchPolicy: 'no-cache',
    onData: ({ data }) => {
      const node = data.data?.backgroundJobProgress?.node;
      const willRefetch = isDetectionFrame(node);
      logger.debug('DocumentDetectionRefetch: job frame', {
        rootId: data.data?.backgroundJobProgress?.rootId,
        kind: node?.kind,
        status: node?.status,
        willRefetch,
      });
      if (willRefetch) {
        scheduleRefetch();
      }
    },
  });

  // Reconcile after the tab regains focus (covers cable drops mid-detection).
  useTabActivity({
    onFocus: () => {
      if (isDetecting) refetch();
    },
  });

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );
}
